//frontend/src/components/CompleteProfile.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Row, Col, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaPassport, FaBriefcase, FaGraduationCap, FaPhone, FaCheck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';

const STEPS = [
    { id: 'personal',     shortTitle: 'Personnel',     title: 'Informations personnelles',    icon: FaUser },
    { id: 'passport',     shortTitle: 'Passeport',      title: 'Passeport',                    icon: FaPassport },
    { id: 'professional', shortTitle: 'Professionnel',  title: 'Situation professionnelle',    icon: FaBriefcase },
    { id: 'education',    shortTitle: 'Éducation',       title: 'Éducation & Langues',          icon: FaGraduationCap },
    { id: 'emergency',    shortTitle: 'Urgence',        title: "Contact d'urgence",            icon: FaPhone },
];

const REQUIRED_BY_STEP = {
    personal:     ['first_name', 'last_name', 'date_of_birth', 'place_of_birth', 'gender', 'nationality', 'email', 'phone', 'address', 'city', 'country'],
    passport:     ['passport_number'],
    professional: ['current_occupation'],
    education:    ['education_level'],
    emergency:    [],
};

const FIELD_LABELS = {
    first_name: 'Prénom', last_name: 'Nom', date_of_birth: 'Date de naissance',
    place_of_birth: 'Lieu de naissance', gender: 'Genre', nationality: 'Nationalité',
    email: 'Email', phone: 'Téléphone', address: 'Adresse', city: 'Ville', country: 'Pays',
    passport_number: 'Numéro de passeport', current_occupation: 'Profession actuelle',
    education_level: "Niveau d'études",
};

const StepIndicator = ({ currentStep }) => (
    <div className="d-flex justify-content-between align-items-flex-start mb-4 position-relative">
        <div className="position-absolute w-100" style={{ height: 2, backgroundColor: '#dee2e6', top: 20, zIndex: 0 }} />
        {STEPS.map((step, index) => {
            const Icon = step.icon;
            const done = index < currentStep;
            const active = index === currentStep;
            return (
                <div key={step.id} className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 1, flex: 1 }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                        style={{
                            width: 40, height: 40,
                            backgroundColor: done ? '#198754' : active ? '#0d6efd' : '#dee2e6',
                            color: done || active ? 'white' : '#6c757d',
                            boxShadow: active ? '0 0 0 4px rgba(13,110,253,0.2)' : 'none',
                            transition: 'all 0.3s',
                            cursor: 'default',
                        }}>
                        {done ? <FaCheck size={13} /> : <Icon size={13} />}
                    </div>
                    <small className="mt-1 text-center d-none d-md-block"
                        style={{ color: active ? '#0d6efd' : done ? '#198754' : '#adb5bd', fontWeight: active ? 600 : 400, fontSize: '0.72rem' }}>
                        {step.shortTitle}
                    </small>
                </div>
            );
        })}
    </div>
);



const CompleteProfile = () => {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [pageLoading, setPageLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [completion, setCompletion] = useState(0);
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        first_name: '', last_name: '', email: '', phone: '',
        date_of_birth: '', place_of_birth: '', gender: '', nationality: '', marital_status: '',
        address: '', city: '', postal_code: '', country: '',
        passport_number: '', passport_issue_date: '', passport_expiry_date: '', passport_issue_place: '',
        current_occupation: '', employer: '', years_of_experience: '', monthly_income: '',
        education_level: '', field_of_study: '', institution: '', graduation_year: '',
        french_level: '', english_level: '', other_languages: '',
        emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
    });

    useEffect(() => {
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProfile = async () => {
        try {
            const response = await api.get('/user');
            const userData = response.data.user;

            // Admins/superadmins shouldn't be here
            if (userData.role === 'admin') { navigate('/admin'); return; }
            if (userData.role === 'superadmin') { navigate('/superadmin'); return; }

            // Already complete → go to dashboard
            if ((userData.profile_completion_percentage || 0) >= 80) {
                navigate('/dashboard');
                return;
            }

            setCompletion(userData.profile_completion_percentage || 0);

            const fmt = (d) => { if (!d) return ''; return new Date(d).toISOString().split('T')[0]; };

            setFormData({
                first_name: userData.first_name || '',
                last_name: userData.last_name || '',
                email: userData.email || '',
                phone: userData.phone || '',
                date_of_birth: fmt(userData.date_of_birth),
                place_of_birth: userData.place_of_birth || '',
                gender: userData.gender || '',
                nationality: userData.nationality || '',
                marital_status: userData.marital_status || '',
                address: userData.address || '',
                city: userData.city || '',
                postal_code: userData.postal_code || '',
                country: userData.country || '',
                passport_number: userData.passport_number || '',
                passport_issue_date: fmt(userData.passport_issue_date),
                passport_expiry_date: fmt(userData.passport_expiry_date),
                passport_issue_place: userData.passport_issue_place || '',
                current_occupation: userData.current_occupation || '',
                employer: userData.employer || '',
                years_of_experience: userData.years_of_experience || '',
                monthly_income: userData.monthly_income || '',
                education_level: userData.education_level || '',
                field_of_study: userData.field_of_study || '',
                institution: userData.institution || '',
                graduation_year: userData.graduation_year || '',
                french_level: userData.french_level || '',
                english_level: userData.english_level || '',
                other_languages: userData.other_languages || '',
                emergency_contact_name: userData.emergency_contact_name || '',
                emergency_contact_phone: userData.emergency_contact_phone || '',
                emergency_contact_relationship: userData.emergency_contact_relationship || '',
            });
        } catch (error) {
            console.error('Error loading profile:', error);
            toast.error('Erreur lors du chargement du profil');
        } finally {
            setPageLoading(false);
        }
    };

    const handleChange = React.useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => {
            if (!prev[name]) return prev;
            const { [name]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const field = (name, label, type = 'text', required = false, extraProps = {}) => (
        <Form.Group className="mb-3">
            <Form.Label>{label}{required && ' *'}</Form.Label>
            <Form.Control
                type={type}
                name={name}
                value={formData[name] ?? ''}
                onChange={handleChange}
                isInvalid={!!errors[name]}
                {...extraProps}
            />
            {errors[name] && <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>}
        </Form.Group>
    );

    const selectFld = (name, label, options, required = false) => (
        <Form.Group className="mb-3">
            <Form.Label>{label}{required && ' *'}</Form.Label>
            <Form.Select
                name={name}
                value={formData[name] ?? ''}
                onChange={handleChange}
                isInvalid={!!errors[name]}
            >
                {options}
            </Form.Select>
            {errors[name] && <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>}
        </Form.Group>
    );

    const validateCurrentStep = () => {
        const stepId = STEPS[currentStep].id;
        const required = REQUIRED_BY_STEP[stepId];
        const newErrors = {};

        required.forEach(field => {
            if (!formData[field] || formData[field].toString().trim() === '') {
                newErrors[field] = `${FIELD_LABELS[field] || field} est requis`;
            }
        });

        if (stepId === 'personal') {
            if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
                newErrors.email = "Format d'email invalide";
            }
            if (formData.date_of_birth) {
                const age = new Date().getFullYear() - new Date(formData.date_of_birth).getFullYear();
                if (age < 18 || age > 100) newErrors.date_of_birth = "L'âge doit être entre 18 et 100 ans";
            }
        }

        if (stepId === 'passport' && formData.passport_issue_date && formData.passport_expiry_date) {
            if (new Date(formData.passport_expiry_date) <= new Date(formData.passport_issue_date)) {
                newErrors.passport_expiry_date = "La date d'expiration doit être postérieure à la date de délivrance";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const saveData = async (silent = false) => {
        const cleanedData = { ...formData };
        // Convert empty strings to null for fields with strict backend validation
        const nullIfEmpty = ['date_of_birth','passport_issue_date','passport_expiry_date',
            'gender','marital_status','education_level','french_level','english_level',
            'years_of_experience','monthly_income','graduation_year'];
        nullIfEmpty.forEach(k => { if (cleanedData[k] === '') cleanedData[k] = null; });

        const response = await api.put('/user', cleanedData);
        if (setUser) setUser(response.data);

        const updated = await api.get('/user');
        if (setUser) setUser(updated.data.user);
        setCompletion(updated.data.user.profile_completion_percentage || 0);

        if (!silent) {
            toast.success('✅ Profil complété avec succès ! Bienvenue sur votre tableau de bord.');
        }
        return updated.data.user;
    };

    const handleNext = async () => {
        if (validateCurrentStep()) {
            setErrors({});
            setSaving(true);
            try {
                await saveData(true);
                toast.info('💾 Étape sauvegardée.');
            } catch (error) {
                console.error('Auto-save error:', error);
                // Don't block navigation on save error
            } finally {
                setSaving(false);
            }
            setCurrentStep(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            toast.warning('⚠️ Veuillez remplir tous les champs obligatoires');
        }
    };

    const handlePrev = () => {
        setErrors({});
        setCurrentStep(prev => prev - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleFinish = async () => {
        if (!validateCurrentStep()) {
            toast.warning('⚠️ Veuillez remplir tous les champs obligatoires');
            return;
        }
        setSaving(true);
        try {
            await saveData(false);
            navigate('/dashboard');
        } catch (error) {
            console.error('Error updating profile:', error);
            if (error.response?.data) {
                const errs = error.response.data;
                const msg = Object.entries(errs).map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(', ') : m}`).join('\n');
                toast.error(`Erreurs de validation:\n${msg}`);
            } else {
                toast.error('❌ Erreur lors de la mise à jour du profil');
            }
        } finally {
            setSaving(false);
        }
    };

    const getCompletionColor = () => {
        if (completion < 50) return 'danger';
        if (completion < 80) return 'warning';
        return 'success';
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0: return (
                <>
                    <h5 className="mb-4">Informations personnelles</h5>
                    <Row><Col md={6}>{field('last_name', 'Nom', 'text', true)}</Col><Col md={6}>{field('first_name', 'Prénoms', 'text', true)}</Col></Row>
                    <Row>
                        <Col md={6}>{field('date_of_birth', 'Date de naissance', 'date', true)}</Col>
                        <Col md={6}>{field('place_of_birth', 'Lieu de naissance', 'text', true)}</Col>
                    </Row>
                    <Row>
                        <Col md={4}>{selectFld('gender', 'Genre', [
                            <option key="" value="">Sélectionner</option>,
                            <option key="M" value="M">Masculin</option>,
                            <option key="F" value="F">Féminin</option>,
                            <option key="O" value="O">Autre</option>,
                        ], true)}</Col>
                        <Col md={4}>{field('nationality', 'Nationalité', 'text', true)}</Col>
                        <Col md={4}>{selectFld('marital_status', 'État civil', [
                            <option key="" value="">Sélectionner</option>,
                            <option key="single" value="single">Célibataire</option>,
                            <option key="married" value="married">Marié(e)</option>,
                            <option key="divorced" value="divorced">Divorcé(e)</option>,
                            <option key="widowed" value="widowed">Veuf/Veuve</option>,
                        ])}</Col>
                    </Row>
                    <Row><Col md={6}>{field('email', 'Email', 'email', true)}</Col><Col md={6}>{field('phone', 'Téléphone', 'tel', true)}</Col></Row>
                    <h6 className="mt-2 mb-3 text-muted">Adresse</h6>
                    {field('address', 'Adresse complète', 'text', true, { as: 'textarea', rows: 2 })}
                    <Row>
                        <Col md={6}>{field('city', 'Ville', 'text', true)}</Col>
                        <Col md={3}>{field('postal_code', 'Code postal')}</Col>
                        <Col md={3}>{field('country', 'Pays', 'text', true)}</Col>
                    </Row>
                </>
            );
            case 1: return (
                <>
                    <h5 className="mb-4">Informations passeport</h5>
                    <Row><Col md={6}>{field('passport_number', 'Numéro de passeport', 'text', true)}</Col><Col md={6}>{field('passport_issue_place', 'Lieu de délivrance')}</Col></Row>
                    <Row>
                        <Col md={6}>{field('passport_issue_date', 'Date de délivrance', 'date')}</Col>
                        <Col md={6}>{field('passport_expiry_date', "Date d'expiration", 'date')}</Col>
                    </Row>
                </>
            );
            case 2: return (
                <>
                    <h5 className="mb-4">Situation professionnelle</h5>
                    <Row><Col md={6}>{field('current_occupation', 'Profession actuelle', 'text', true)}</Col><Col md={6}>{field('employer', 'Employeur')}</Col></Row>
                    <Row>
                        <Col md={6}>{field('years_of_experience', "Années d'expérience", 'number')}</Col>
                        <Col md={6}>{field('monthly_income', 'Revenu mensuel (€)', 'number')}</Col>
                    </Row>
                </>
            );
            case 3: return (
                <>
                    <h5 className="mb-4">Formation & Langues</h5>
                    <Row>
                        <Col md={6}>{selectFld('education_level', "Niveau d'études", [
                            <option key="" value="">Sélectionner</option>,
                            <option key="high_school" value="high_school">Lycée</option>,
                            <option key="bachelor" value="bachelor">Licence / Bachelor</option>,
                            <option key="master" value="master">Master</option>,
                            <option key="doctorate" value="doctorate">Doctorat</option>,
                            <option key="other" value="other">Autre</option>,
                        ], true)}</Col>
                        <Col md={6}>{field('field_of_study', "Domaine d'études")}</Col>
                    </Row>
                    <Row>
                        <Col md={8}>{field('institution', 'Institution / Université')}</Col>
                        <Col md={4}>{field('graduation_year', "Année d'obtention", 'number')}</Col>
                    </Row>
                    <h6 className="mt-3 mb-3 text-muted">Compétences linguistiques</h6>
                    <Row>
                        <Col md={6}>{selectFld('french_level', 'Niveau de français', [
                            <option key="" value="">Sélectionner</option>,
                            ...['A1','A2','B1','B2','C1','C2'].map(l => <option key={l} value={l}>{l}</option>),
                        ])}</Col>
                        <Col md={6}>{selectFld('english_level', "Niveau d'anglais", [
                            <option key="" value="">Sélectionner</option>,
                            ...['A1','A2','B1','B2','C1','C2'].map(l => <option key={l} value={l}>{l}</option>),
                        ])}</Col>
                    </Row>
                    {field('other_languages', 'Autres langues', 'text', false, { as: 'textarea', rows: 2, placeholder: 'Ex: Espagnol (B1), Allemand (A2)' })}
                </>
            );
            case 4: return (
                <>
                    <h5 className="mb-4">Contact d'urgence <span className="text-muted fs-6 fw-normal">(optionnel)</span></h5>
                    {field('emergency_contact_name', 'Nom complet')}
                    <Row>
                        <Col md={6}>{field('emergency_contact_phone', 'Téléphone', 'tel')}</Col>
                        <Col md={6}>{field('emergency_contact_relationship', 'Relation', 'text', false, { placeholder: "Ex: Père, Mère, Conjoint(e)" })}</Col>
                    </Row>
                </>
            );
            default: return null;
        }
    };

    if (pageLoading) {
        return (
            <Container className="py-5 text-center">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3 text-muted">Chargement de votre profil...</p>
            </Container>
        );
    }

    return (
        <Container className="py-4" style={{ maxWidth: 760 }}>
            <div className="mb-4">
                <h2 className="mb-1"><FaUser className="me-2 text-primary" />Compléter mon profil</h2>
                <p className="text-muted mb-0">Remplissez toutes les étapes pour pouvoir créer votre dossier de demande de visa.</p>
            </div>

            {/* Progress bar */}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body className="py-3">
                    <div className="d-flex justify-content-between mb-2">
                        <span className="fw-semibold">Profil complété</span>
                        <span className="fw-bold">{completion}%</span>
                    </div>
                    <ProgressBar now={completion} variant={getCompletionColor()} style={{ height: 8, borderRadius: 4 }} />
                </Card.Body>
            </Card>

            {/* Step indicator */}
            <StepIndicator currentStep={currentStep} />

            {/* Step title */}
            <div className="mb-3 mt-2">
                <span className="text-muted small">Étape {currentStep + 1} sur {STEPS.length}</span>
                <h4 className="mb-0 text-primary fw-bold">{STEPS[currentStep].title}</h4>
            </div>

            {/* Step content */}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body className="p-4">
                    {renderStepContent()}
                </Card.Body>
            </Card>

            {/* Navigation buttons */}
            <div className="d-flex justify-content-between align-items-center">
                <Button variant="outline-secondary" onClick={handlePrev} disabled={currentStep === 0} style={{ minWidth: 130 }}>
                    <FaChevronLeft className="me-2" size={12} />Précédent
                </Button>

                <span className="text-muted small">{currentStep + 1} / {STEPS.length}</span>

                {currentStep < STEPS.length - 1 ? (
                    <Button variant="primary" onClick={handleNext} disabled={saving} style={{ minWidth: 130 }}>
                        {saving ? (
                            <><span className="spinner-border spinner-border-sm me-2" />Sauvegarde...</>
                        ) : (
                            <>Suivant<FaChevronRight className="ms-2" size={12} /></>
                        )}
                    </Button>
                ) : (
                    <Button variant="success" onClick={handleFinish} disabled={saving} style={{ minWidth: 130 }}>
                        {saving ? (
                            <><span className="spinner-border spinner-border-sm me-2" />Enregistrement...</>
                        ) : (
                            <><FaCheck className="me-2" size={12} />Terminer</>
                        )}
                    </Button>
                )}
            </div>
        </Container>
    );
};

export default CompleteProfile;
